import {
  IsNotEmpty,
  IsOptional,
  IsIn,
  IsArray,
  IsDate,
  IsNumber,
  IsInt,
  IsBoolean,
  IsObject,
  IsString,
  MaxLength,
  Min,
  ValidateNested,
} from "class-validator";
import { Type } from "class-transformer";

import { Constants } from "@eagerdog/constants";
import { ShowElementDto } from "./show-element.dto";
import { ShowRunTimeSlotDto } from "./show-run-time-slot.dto";
import { BreedJudgeDto } from "./breed-judge.dto";
import { BreedGroupJudgeDto } from "./breed-group-judge.dto";
import { ShowRingDto } from "./show-ring.dto";
import { ShowScentDto } from "./show-scent.dto";
import { AkcAgilitySctEntryDto, AkcAgilitySctDto } from "./show-akc-agility-sct.dto";
import { WeightPullClassSetupDto } from "./show-weight-pull-setup.dto";
import { JudgeEntryLimitDto } from "./judge-entry-limit.dto";

// ----------
export class ShowDto {
  @IsNotEmpty()
  show_name: string;

  @IsOptional()
  club_name?: string;

  @IsOptional()
  show_club_id?: string;

  @IsNotEmpty()
  @IsIn(Object.values(Constants.show_type))
  show_type: string;

  @IsOptional()
  event_number?: string;

  @IsNotEmpty()
  @IsDate()
  show_date: Date;

  @IsOptional()
  @IsBoolean()
  trailing_show?: boolean;

  @IsOptional()
  previous_show_name?: string;

  @IsOptional()
  previous_show_id?: string;

  @IsBoolean()
  use_registration_end_date: boolean = false;

  @IsOptional()
  @IsDate()
  registration_end_date?: Date;

  // fees
  @IsNotEmpty()
  @IsNumber()
  fee: number;

  @IsBoolean()
  use_same_fee: boolean = true;

  @IsBoolean()
  use_early_bird_fee: boolean;

  @IsOptional()
  @IsDate()
  early_bird_end_date?: Date;

  @IsOptional()
  @IsNumber()
  early_bird_fee?: number;

  @IsOptional()
  @IsNumber()
  junior_fee?: number;

  @IsOptional()
  @IsNumber()
  early_bird_junior_fee?: number;

  // FEO (For Exhibition Only) — C-WAGS show types
  @IsOptional()
  @IsBoolean()
  feo_available?: boolean;

  @IsOptional()
  @IsNumber()
  @Min(0)
  feo_fee?: number;

  @IsOptional()
  @IsBoolean()
  is_archived?: boolean;

  @IsOptional()
  @IsInt()
  max_dogs?: number;

  @IsOptional()
  @IsInt()
  first_run_time_minutes?: number; //minutes of the day

  @IsOptional()
  @IsInt()
  run_duration_minutes?: number; //minutes

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ShowRunTimeSlotDto)
  time_slots?: ShowRunTimeSlotDto[];

  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ShowElementDto)
  show_elements: ShowElementDto[];

  // @IsOptional()
  // @IsArray()
  // @ValidateNested({ each: true })
  // @Type(() => RunningOrderDto)
  // running_order?: RunningOrderDto[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => BreedJudgeDto)
  breed_judges?: BreedJudgeDto[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => BreedGroupJudgeDto)
  breed_group_judges?: BreedGroupJudgeDto[];

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ShowScentDto)
  scents?: ShowScentDto[];

  @IsOptional()
  @IsObject()
  @ValidateNested({ each: true })
  akc_agility_sct?: AkcAgilitySctDto;

  @IsOptional()
  arm_number_start?: number;

  @IsOptional()
  specialty_breed?: string;

  @IsOptional()
  @IsArray()
  excluded_breeds?: string[];

  @IsOptional()
  @IsDate()
  excluded_breeds_end_date?: Date;

  @IsOptional()
  @IsString()
  breed_restriction_mode?: string;

  @IsOptional()
  @IsArray()
  allowed_breeds?: string[];

  @IsOptional()
  @IsDate()
  allowed_breeds_end_date?: Date;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => ShowRingDto)
  rings?: ShowRingDto[];

  @IsOptional()
  @IsIn(Object.values(Constants.waitlist_options))
  waitlist_option?: string; //Constants.waitlist_options

  @IsOptional()
  @IsNumber()
  waitlist_limit?: number;

  @IsOptional()
  @IsBoolean()
  waitlist_enabled?: boolean;

  @IsOptional()
  @IsNumber()
  waitlist_size?: number;

  @IsOptional()
  @IsNumber()
  detective_run_limit?: number;

  @IsOptional()
  @IsInt()
  @Min(0)
  detective_manual_entries?: number;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => JudgeEntryLimitDto)
  judge_entry_limits?: JudgeEntryLimitDto[];

  @IsOptional()
  @Type(() => Date)
  @IsDate()
  manual_entry_limit_date?: Date;

  @IsOptional()
  @IsNumber()
  act?: number;

  @IsOptional()
  @IsBoolean()
  show_run_order?: boolean;

  @IsOptional()
  @IsIn(Object.values(Constants.trial_status))
  show_status?: string;

  @IsOptional()
  @IsString()
  @MaxLength(50)
  show_status_note?: string;

  @IsOptional()
  @IsBoolean()
  pay_club_directly_enabled?: boolean;

  @IsOptional()
  @IsBoolean()
  round_start_times_to_5?: boolean;

  @IsOptional()
  @IsArray()
  run_offs?: any[]; // IRunOff[] - keeping as any[] for DTO flexibility

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => WeightPullClassSetupDto)
  weight_pull_setups?: WeightPullClassSetupDto[];

  @IsOptional()
  @IsIn(Object.values(Constants.rally_course_mode))
  rally_course_mode?: string;

  @IsOptional()
  @IsArray()
  @ValidateNested({ each: true })
  @Type(() => RallyCourseImageDto)
  rally_course_images?: RallyCourseImageDto[];

  @IsOptional()
  @IsArray()
  @IsString({ each: true })
  hidden_bis_classes?: string[];
}

export class RallyCourseImageDto {
  @IsString()
  level: string;

  @IsString()
  image_url: string;
}
